// Shared contracts between api, worker and web. See CONTRACTS.md.
export const POLL_INTERVAL_MS = {
    normal: 5 * 60_000,
    heightened: 60_000,
    // 45s, not 30s: NWS does not republish a warning faster than this in practice, and the
    // tighter cadence bought nothing but upstream requests against an edge that throttles
    // this deployment in bursts.
    alert: 45_000,
    recovery: 60_000,
};
export const RECOVERY_DURATION_MS = 10 * 60_000;
/** NWS feed is considered STALE when last successful poll is older than this. */
export const STALE_AFTER_MS = 12 * 60_000;
/**
 * Floor between real upstream calls to NWS, whatever the poll interval.
 *
 * The ETag path does not save a request: NWS embeds a per-response agent token in the
 * ETag (`W/"<hash>:dtagent…:dtagent…"`), so `If-None-Match` never matches and every poll
 * returns a full 200 body. Alert mode polls every 30s, which meant re-downloading the
 * same ~34KB from one IP twice a minute — and NOAA's edge answers that with bursts of
 * 403. Within this window the cached body in Dragonfly is served instead, which keeps
 * tenant evaluation on its own cadence without hammering the upstream.
 */
export const NWS_MIN_UPSTREAM_INTERVAL_MS = 20_000;
/**
 * How many polls in a row must fail before the NWS component is called degraded.
 *
 * NOAA throttling comes in short bursts that clear on their own, and flagging the whole
 * system amber for one 403 — while cached alerts are still being served correctly —
 * trains operators to ignore the health page.
 */
export const NWS_DEGRADE_AFTER_FAILURES = 3;
/** A radar status older than this is stale: reported as unknown rather than as fact. */
export const RADAR_STATUS_TTL_SECONDS = 15 * 60;
/**
 * Winter products that should notify. Exported so the tenant migration unions exactly
 * this set into policies that predate it — changing the default alone reaches nobody,
 * because every tenant stores its own copy of `eventTypes`.
 */
export const WINTER_EVENTS = [
    'Ice Storm Warning',
    'Winter Storm Warning',
    'Blizzard Warning',
    'Snow Squall Warning',
    'Winter Storm Watch',
    'Winter Weather Advisory',
];
export const DEFAULT_ALERT_POLICY = {
    eventTypes: [
        'Tornado Warning',
        'Severe Thunderstorm Warning',
        'Flash Flood Warning',
        'Tornado Watch',
        'Severe Thunderstorm Watch',
        ...WINTER_EVENTS,
    ],
    // Ice bypasses the threshold: it is the event that ends with a county off the grid.
    alwaysAlert: ['Tornado Warning', 'Ice Storm Warning'],
    windThresholdMph: 50,
    hailThresholdIn: 1.0,
    iceThresholdIn: 0.1,
    snowThresholdIn: 2,
    defaultRadiusMiles: 10,
    cooldownMinutes: 30,
    notifyOnMaterialChange: true,
    smsEnabled: true,
};
export * from './motion.js';
export * from './eta.js';
export * from './sound.js';
export * from './closures.js';
export * from './lightning.js';
export * from './publicmap.js';
export * from './listhub.js';
export const AREA_KINDS = ['county', 'city', 'custom'];
export const AREA_KIND_LABELS = {
    county: 'County',
    city: 'City / place',
    custom: 'Custom area',
};
/**
 * What this tenant is actually watching, for any "which area am I looking at" label.
 *
 * Coverage can be a county, a city or a drawn polygon, so it is tracked separately
 * from the tenant's own name — changing coverage must not rename the customer.
 * Falls back to countyName for tenants seeded before coverage areas existed.
 */
export function coverageLabel(tenant) {
    return tenant?.area?.label || tenant?.countyName || tenant?.name || 'your area';
}
/** Most extra alert locations one tenant may watch on top of its coverage area. */
export const MAX_WATCH_AREAS = 20;
export const STATE_NAMES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
    FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
    IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
    ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
    MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
    NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
    NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
    PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
    TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
    WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    AS: 'American Samoa', GU: 'Guam', MP: 'Northern Mariana Islands',
    PR: 'Puerto Rico', VI: 'U.S. Virgin Islands',
};
/** Dropdown source: every state we can look up, sorted by name. */
export const US_STATES = Object.keys(STATE_NAMES)
    .map((code) => ({ code, name: STATE_NAMES[code] }))
    .sort((a, b) => a.name.localeCompare(b.name));
export function isUspsState(s) {
    return !!s && Object.prototype.hasOwnProperty.call(STATE_NAMES, s.trim().toUpperCase());
}
/* ---------------------------------------------------------------------------
 * SMS gateway
 *
 * Every tenant sends through its own gateway credentials, so one county's key is
 * never used to text another county's staff and a revoked key takes down exactly
 * one tenant. The key itself lives in the `sms_credentials` collection, never on
 * the tenant document — admin routes hand whole tenant docs to the browser, and a
 * secret on that document would leak the moment someone opened the tenants table.
 * ------------------------------------------------------------------------- */
export const SMS_PROVIDERS = ['easysmsrelay', 'webhook', 'none'];
export const SMS_PROVIDER_LABELS = {
    easysmsrelay: 'Easy SMS Relay',
    webhook: 'Generic webhook (server SMS_WEBHOOK_URL)',
    none: 'Off — log only',
};
export const EASY_SMS_RELAY_BASE_URL = 'https://easysmsrelay.com';
/** POST here with `{to, body}`; requires an API key with the `messages:send` scope. */
export const EASY_SMS_RELAY_SEND_PATH = '/api/v1/messages/send';
export const DEFAULT_TENANT_SMS_SETTINGS = {
    provider: 'easysmsrelay',
    baseUrl: EASY_SMS_RELAY_BASE_URL,
    configured: false,
    keyHint: null,
    updatedAt: null,
    updatedBy: null,
    lastTestAt: null,
    lastTestResult: null,
    lastTestDetail: null,
};
/** Masked form of an API key, safe to store and display: `relay_a1b2…7f8g`. */
export function smsKeyHint(key) {
    const k = key.trim();
    if (k.length <= 10)
        return `${k.slice(0, 2)}…`;
    return `${k.slice(0, 10)}…${k.slice(-4)}`;
}
/**
 * Easy SMS Relay keys look like `relay_xxxxxxxxxxxxxxxx`. Checked loosely on purpose —
 * rejecting an unfamiliar but valid key would lock an operator out of their own gateway,
 * so only obvious mistakes (whitespace, a pasted curl line, an empty box) are refused.
 */
export function isPlausibleApiKey(key) {
    const k = key.trim();
    return k.length >= 12 && k.length <= 200 && !/\s/.test(k);
}
/**
 * Best-effort E.164 for a recipient. Recipients were free-text "opaque identifiers"
 * before there was a real gateway, so existing tenants hold everything from
 * `555-123-4567` to `+15551234567`; the relay only accepts E.164.
 * Returns null when it cannot be read as a phone number, and the caller reports that
 * recipient as failed rather than silently dropping it.
 */
export function toE164(raw, defaultCountry = '1') {
    const trimmed = (raw ?? '').trim();
    if (!trimmed)
        return null;
    const plus = trimmed.startsWith('+');
    const digits = trimmed.replace(/\D/g, '');
    if (!digits)
        return null;
    if (plus)
        return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
    if (digits.length === 10)
        return `+${defaultCountry}${digits}`;
    if (digits.length === 11 && digits.startsWith('1'))
        return `+${digits}`;
    return null;
}
/** Pure request description for a single relay send, so both api and worker agree on it. */
export function relaySendRequest(gateway, to, body) {
    const base = (gateway.baseUrl || EASY_SMS_RELAY_BASE_URL).replace(/\/+$/, '');
    return {
        url: `${base}${EASY_SMS_RELAY_SEND_PATH}`,
        headers: {
            Authorization: `Bearer ${gateway.apiKey}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ to, body }),
    };
}
/**
 * Turns a relay response into a one-line reason. 401/403 are the two an operator can
 * actually fix, so they are named rather than reported as a bare status code, and an
 * opted-out number is called out because no retry will ever deliver it.
 */
export function relayFailureDetail(status, payload) {
    const body = (payload ?? {});
    if (body.status === 'blocked_opt_out')
        return 'recipient has opted out';
    if (status === 401)
        return 'relay rejected the API key (401) — it is invalid or revoked';
    if (status === 403)
        return 'API key is missing the messages:send scope (403)';
    if (status === 429)
        return 'relay rate limit (429)';
    const detail = body.error || body.message;
    return detail ? `relay returned ${status}: ${detail}` : `relay returned ${status}`;
}
/** A send is only a success when the relay both accepted it and did not block it. */
export function relayAccepted(status, payload) {
    if (status < 200 || status >= 300)
        return false;
    const body = (payload ?? {});
    return body.status !== 'blocked_opt_out';
}
export function tenantReadiness(t, counts) {
    const issues = [];
    if (!t.boundary) {
        issues.push({
            code: 'no-boundary',
            severity: 'error',
            message: 'No coverage area. Alerts with a polygon cannot be matched to this tenant.',
        });
    }
    if (!t.zoneCodes || t.zoneCodes.length === 0) {
        issues.push({
            code: 'no-zones',
            severity: 'error',
            message: 'No NWS zone codes. Watches and other alerts issued without a polygon will never reach this tenant.',
        });
    }
    const smsEnabled = t.alertPolicy?.smsEnabled !== false;
    const recipients = (t.notificationGroups ?? []).reduce((n, g) => n + (g.recipients?.length ?? 0), 0);
    if (smsEnabled && recipients === 0) {
        issues.push({
            code: 'no-recipients',
            severity: 'error',
            message: 'SMS is enabled but no recipients are configured. Notifications will be logged as failed.',
        });
    }
    if (smsEnabled && t.sms && t.sms.provider === 'easysmsrelay' && !t.sms.configured) {
        issues.push({
            code: 'no-sms-key',
            severity: 'error',
            message: 'SMS is enabled but this tenant has no Easy SMS Relay API key. Notifications will be recorded as failed.',
        });
    }
    if (!smsEnabled) {
        issues.push({ code: 'sms-disabled', severity: 'warn', message: 'SMS notifications are turned off.' });
    }
    if (counts && (counts.users ?? 0) === 0) {
        issues.push({ code: 'no-users', severity: 'warn', message: 'No users yet — nobody can sign in to this tenant.' });
    }
    return issues;
}
/** USPS code -> Census state FIPS, for TIGERweb lookups. */
export const STATE_FIPS = {
    AL: '01', AK: '02', AZ: '04', AR: '05', CA: '06', CO: '08', CT: '09', DE: '10',
    DC: '11', FL: '12', GA: '13', HI: '15', ID: '16', IL: '17', IN: '18', IA: '19',
    KS: '20', KY: '21', LA: '22', ME: '23', MD: '24', MA: '25', MI: '26', MN: '27',
    MS: '28', MO: '29', MT: '30', NE: '31', NV: '32', NH: '33', NJ: '34', NM: '35',
    NY: '36', NC: '37', ND: '38', OH: '39', OK: '40', OR: '41', PA: '42', RI: '44',
    SC: '45', SD: '46', TN: '47', TX: '48', UT: '49', VT: '50', VA: '51', WA: '53',
    WV: '54', WI: '55', WY: '56', AS: '60', GU: '66', MP: '69', PR: '72', VI: '78',
};
export const ASSET_TYPES = [
    "courthouse", "law", "jail", "water", "pump", "fire", "radio", "school", "custom",
];
export const ASSET_TYPE_LABELS = {
    courthouse: "Courthouse", law: "Law enforcement", jail: "Jail", water: "Water tower",
    pump: "Pump station", fire: "Fire station", radio: "Radio tower", school: "School", custom: "Custom",
};
/* ---------- public alert links (the short URL an SMS points at) ---------- */
/**
 * A text message is read on a phone, at night, by someone who may be driving to the
 * scene. `/alerts/urn%3Aoid%3A2.49.0.1.840.0.0d78…` wraps to eight lines, eats SMS
 * segments and invites carriers to mangle it, so a notification links to a short code
 * instead. The code resolves to a read-only view of that one warning — no sign-in, and
 * no access to anything else in the tenant.
 */
export const ALERT_LINK_CODE_LENGTH = 7;
/**
 * Codes outlive the warning by a few days so a forwarded text still opens something,
 * then stop resolving. An unguessable code is not a secret once it is in a group chat.
 */
export const ALERT_LINK_TTL_HOURS = 72;
/** No 0/O/1/I/l: these get read aloud over a radio and typed in by hand. */
export const ALERT_LINK_ALPHABET = '23456789abcdefghjkmnpqrstuvwxyz';
/** Dragonfly pub/sub channel. Message = JSON {type: SseEventName, tenantId: string|null, data?: unknown} */
export const PUBSUB_CHANNEL = 'ww:events';
/** Dragonfly keys */
export const KEYS = {
    // Scoped by the polled area set: onboarding a tenant in a new state changes that set,
    // and a shared key would serve a stale 304 from the previous scope for up to 15m.
    nwsRawFor: (areaKey) => `ww:nws:raw:${areaKey}`, // raw upstream body, TTL 15m
    nwsEtagFor: (areaKey) => `ww:nws:etag:${areaKey}`,
    // Presence means "fetched recently"; TTL is the upstream floor. Separate from the raw
    // body, which is kept far longer so it can still be served when NOAA is refusing us.
    nwsFreshFor: (areaKey) => `ww:nws:fresh:${areaKey}`,
    activeAlerts: 'ww:alerts:active', // JSON Alert[] TTL 30m
    tenantEvals: (tenantId) => `ww:tenant:${tenantId}:evals`, // JSON AlertEvaluation[]
    status: 'ww:status', // JSON WorkerStatus
    heartbeat: 'ww:worker:heartbeat', // ISO string, TTL 90s
    pollLock: 'ww:lock:nws-poll', // SET NX PX
    dedupe: (tenantId, nwsId, version) => `ww:dedupe:${tenantId}:${nwsId}:${version}`,
    cooldown: (tenantId, nwsId) => `ww:cooldown:${tenantId}:${nwsId}`,
    radarTile: (hash) => `ww:radar:tile:${hash}`, // TTL 120s
    radarStatus: 'ww:radar:status', // JSON ComponentHealth
    loginAttempts: (ip) => `ww:ratelimit:login:${ip}`,
};
export function categorize(event) {
    const e = event.toLowerCase();
    if (e.endsWith('warning') || e.includes('emergency'))
        return 'warning';
    if (e.endsWith('watch'))
        return 'watch';
    if (e.endsWith('advisory'))
        return 'advisory';
    return 'statement';
}
/**
 * Winter branches come FIRST, deliberately. The generic `warning` branch below is a
 * catch-all, so "Ice Storm Warning" used to fall into `svr` and render pixel-identical
 * to a Severe Thunderstorm Warning — on a map whose legend calls that colour
 * "Svr T-storm Warning". During an ice event the map said thunderstorm.
 *
 * `hasIce` lets a Winter Weather Advisory be classified by what the product actually
 * says rather than by its title: that one product carries snow, sleet AND freezing rain,
 * and its name never tells you which.
 */
export function alertKind(event, hasIce = false) {
    const e = event.toLowerCase();
    if (e.includes("tornado warning"))
        return "tor";
    if (e.includes("ice storm") ||
        e.includes("freezing rain") ||
        e.includes("freezing drizzle") ||
        e.includes("ice accretion"))
        return "ice";
    if (e.includes("winter weather advisory"))
        return hasIce ? "ice" : "snow";
    if (e.includes("winter storm") ||
        e.includes("blizzard") ||
        e.includes("snow squall") ||
        e.includes("lake effect snow") ||
        e.includes("heavy snow"))
        return "snow";
    if (e.includes("warning"))
        return e.includes("flood") ? "flood" : "svr";
    if (e.includes("watch"))
        return "watch";
    if (e.includes("flood"))
        return "flood";
    return "sps";
}
export const KIND_COLORS = {
    tor: { color: "#ff7a7f", fill: "#ff3b47", tint: "rgba(255,59,71,.16)" },
    // Violet for ice and pink for snow, following NWS convention so an operator reads them
    // without consulting the legend. Neither may be worn by an annotation.
    ice: { color: "#e08cff", fill: "#a855f7", tint: "rgba(168,85,247,.16)" },
    snow: { color: "#ffa8cd", fill: "#ff69b4", tint: "rgba(255,105,180,.14)" },
    svr: { color: "#ffa857", fill: "#ff8c1a", tint: "rgba(255,140,26,.15)" },
    watch: { color: "#f7cf4f", fill: "#f5c518", tint: "rgba(245,197,24,.13)" },
    flood: { color: "#4fd6ef", fill: "#00a9c7", tint: "rgba(0,169,199,.14)" },
    sps: { color: "#e9cc9e", fill: "#d9b27a", tint: "rgba(217,178,122,.13)" },
};
export function alertColor(event) {
    return KIND_COLORS[alertKind(event)].fill;
}
//# sourceMappingURL=index.js.map